import React from 'react';
import floor from 'lodash/floor';
import isEmpty from 'lodash/isEmpty';
import moment from 'moment';

import { AUDIENCE_STATUS_MAPPING } from 'pages/presets/poi-editor/services/constants';

// Constants for recently added audience detection
export const RECENTLY_ADDED_AUDIENCE_THRESHOLD_DAYS = 7;

export const MILLISECONDS_PER_DAY = 1000 * 60 * 60 * 24;

const customAudiencesHierarchy = {
    label: 'Custom Audiences',
    value: 'custom_audiences',
    categories: [
        {
            // For Audience page filtering by checkboxes and other uses
            label: 'Custom Defined',
            value: 'custom_defined',
            source: 'pds_custom_defined',

            // For Audience Tabs landing pages
            type: 'Custom Audiences',
            link: '/audiences/pds-custom',
            icon: <img src="/images/audience-segments/icons/custom defined-card image.png" />,
            tooltip:
                'Precision Targeting: Defined by specific targeting parameters, this segment optimizes impressions to focus on the specific consumers most likely to engage.',

            // For segment picker filtering in ad form page
            valuesForSegmentPicker: ['pds_custom_defined'],
            category: 'custom_audiences',
            shouldHide: ({ restrictedSegments }) => {
                return _.intersection(restrictedSegments, ['pds_custom_defined']).length > 0;
            },
        },
        {
            // For Audience page filtering by checkboxes and other uses
            label: 'Custom Developed',
            value: 'custom_development',
            source: 'pds_custom_developed',

            // For Audience Tabs landing pages
            link: '/audiences/custom-developed',
            type: 'Custom Audiences',
            tooltip:
                'Advanced Engineering: Designed to reach highly specialized segments, this solution addresses unique business challenges through a custom-built targeting strategy.',
            icon: <img src="/images/audience-segments/icons/customd eveloped-card image.png" />,

            // For segment picker filtering in ad form page
            valuesForSegmentPicker: ['pds_custom_developed'],
            category: 'custom_audiences',
            shouldHide: ({ restrictedSegments }) => {
                return _.intersection(restrictedSegments, ['pds_custom_developed']).length > 0;
            },
        },
        {
            // For Audience page filtering by checkboxes and other uses
            label: 'Custom Flex Geolocation',
            value: 'flex_geolocation_custom',
            source: 'flex_custom_brand',

            // For Audience Tabs landing pages
            link: '/audiences/flex-custom',
            type: 'Custom Audiences',
            icon: (
                <img src="/images/audience-segments/icons/custom flex geolocation-card image.png" />
            ),
            tooltip:
                'Focus on Proximity: Capture the attention of consumers aligned with your physical location or media exposure to drive efficiency and conquesting strategies.',

            // For segment picker filtering in ad form page
            valuesForSegmentPicker: ['flex_custom_brand'],
            category: 'custom_audiences',
            shouldHide: ({
                restrictedSegments,
                platform,
                shouldHideFlexSegments,
                isCrossPlatformCampaign,
            }) => {
                if (
                    shouldHideFlexSegments ||
                    _.intersection(restrictedSegments, ['flex_custom_brand']).length > 0
                ) {
                    return true;
                }

                if (isCrossPlatformCampaign) {
                    return false;
                } else {
                    return platform !== 'inapp';
                }
            },
        },
        {
            // For Audience page filtering by checkboxes and other uses
            label: 'Retargeting',
            value: 'retargeting',
            source: ['ad', 'campaign', 'pixel'], // availableCategories from /client/packages/pages/audiences/overall-dashboard/v2/component.js had only ['ad', 'campaign']
            // source: 'retargeting', // DISCREPANCY: filtersByTypeRestructured has both array and string

            // For Audience Tabs landing pages
            link: '/audiences/retargeting',
            type: 'Custom Audiences',
            icon: <img src="/images/audience-segments/icons/retargeting-card image.png" />,
            tooltip:
                'Capitalize on Engagement: Ensure your messaging reaches the most relevant audience by retargeting users who have already demonstrated a verified interest in your brand.',

            // For segment picker filtering in ad form page
            valuesForSegmentPicker: ['ad', 'campaign', 'pixel'],
            category: 'custom_audiences',
            shouldHide: ({ restrictedSegments }) => {
                return _.intersection(restrictedSegments, ['ad', 'campaign', 'pixel']).length > 0;
            },
        },
    ],
};

// New audience category hierarchy with audience restructuring
export const AUDIENCE_CATEGORY_HIERARCHY = [
    {
        label: 'Pelmorex Data',
        value: 'pelmorex_data',
        // description:
        //     'These are audience categories that Pelmorex compiled so that you don’t have to do the work. Feel free to explore each category to find your target audience.',
        sections: [
            {
                label: 'Standard Audiences',
                value: 'standard_audiences',
                categories: [
                    {
                        // For Audience page filtering by checkboxes and other uses
                        label: 'Category Visitation',
                        value: 'category_visitation',
                        source: 'pds_category',

                        // For Audience Tabs landing pages
                        type: 'Standard Audiences',
                        link: '/audiences/category-visitation',
                        icon: (
                            <img src="/images/audience-segments/icons/category visitation-card image.png" />
                        ),
                        tooltip:
                            'Build Category Dominance: Connect with valuable consumers who demonstrate a strong affinity for your brand category to drive reach and share-of-voice.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_category'],
                        category: 'pelmorex_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return _.intersection(restrictedSegments, ['pds_category']).length > 0;
                        },
                    },
                    {
                        // For Audience page filtering by checkboxes and other uses
                        label: 'Brand Visitation',
                        value: 'brand_visitation',
                        source: 'pds_brand',

                        // For Audience Tabs landing pages
                        type: 'Standard Audiences',
                        link: '/audiences/brand-visitation',
                        icon: (
                            <img src="/images/audience-segments/icons/brand visitation-card image.png" />
                        ),
                        tooltip:
                            'Conquest & Defend: Target consumers actively engaged with specific brands to strengthen loyalty, drive conversions, or conquest competitors.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_brand'],
                        category: 'pelmorex_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return _.intersection(restrictedSegments, ['pds_brand']).length > 0;
                        },
                    },
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Behavioural Visitation',
                        label: 'Behavioural Visitation',
                        value: 'behaviour_visitation',
                        source: 'pds_behavioural',

                        // For Audience Tabs landing pages
                        type: 'Standard Audiences',
                        link: '/audiences/behavioural-visitation',
                        icon: (
                            <img src="/images/audience-segments/icons/behavioural visitation-card image.png" />
                        ),
                        tooltip:
                            'Influence Invested Consumers: Align your brand with the routines of consumers who demonstrate genuine enthusiasm for a particular lifestyle.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_behavioural'],
                        category: 'pelmorex_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return (
                                _.intersection(restrictedSegments, ['pds_behavioural']).length > 0
                            );
                        },
                    },
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Interest & Intent',
                        label: 'Interest & Intent',
                        value: 'pds_intent',
                        source: 'pds_intent',

                        // For Audience Tabs landing pages
                        type: 'Standard Audiences',
                        link: '/audiences/intent',
                        icon: (
                            <img src="/images/audience-segments/icons/interest and intent-card image.png" />
                        ),
                        tooltip:
                            'Capture Early Intent: Connect with consumers who exhibit a need or interest during the initial phases of their decision-making journey.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_intent'],
                        category: 'pelmorex_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return _.intersection(restrictedSegments, ['pds_intent']).length > 0;
                        },
                    },
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Flex Category', // DISCREPANCY: filtersByTypeRestructured has "Flex Geolocation"
                        // title: 'Flex Geolocation', // from filtersByTypeRestructured
                        label: 'Flex Category',
                        value: 'flex_geolocation_pelmorex',
                        source: ['flex_standard_brand', 'flex_location_category'],

                        // For Audience Tabs landing pages
                        type: 'Standard Audiences',
                        link: '/audiences/flex-brand',
                        icon: (
                            <img src="/images/audience-segments/icons/flex category-card image.png" />
                        ),
                        tooltip:
                            'Local Relevance: Drive awareness and encourage visitation by targeting audiences with physical relevance to specific category locations.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['flex_standard_brand', 'flex_location_category'],
                        category: 'pelmorex_data',
                        shouldHide: ({
                            restrictedSegments,
                            shouldHideFlexSegments,
                            platform,
                            isCrossPlatformCampaign,
                        }) => {
                            if (
                                shouldHideFlexSegments ||
                                _.intersection(restrictedSegments, [
                                    'flex_standard_brand',
                                    'flex_location_category',
                                ]).length > 0
                            ) {
                                return true;
                            }

                            if (isCrossPlatformCampaign) {
                                return false;
                            } else {
                                return platform !== 'inapp';
                            }
                        },
                    },
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Declared Age',
                        label: 'Declared Age',
                        value: 'declared_age',
                        source: 'pds_declared_age',

                        // For Audience Tabs landing pages
                        type: 'Standard Audiences',
                        link: '/audiences/age',
                        icon: (
                            <img src="/images/audience-segments/icons/declared age-card image.png" />
                        ),
                        tooltip:
                            'Regulatory Compliance: Uphold brand safety and eligibility standards by utilizing self-declared insights to target consumers within compliant age brackets.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_declared_age'],
                        category: 'pelmorex_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return (
                                _.intersection(restrictedSegments, ['pds_declared_age']).length > 0
                            );
                        },
                    },
                ],
            },
            {
                title: 'Advanced Audiences',
                label: 'Advanced Audiences',
                value: 'advanced_audiences',
                categories: [
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Seasonal',
                        label: 'Seasonal',
                        value: 'pds_seasonal',
                        source: 'pds_seasonal',

                        // For Audience Tabs landing pages
                        type: 'Advanced Audiences',
                        link: '/audiences/seasonal',
                        icon: <img src="/images/audience-segments/icons/seasonal-card image.png" />,
                        tooltip:
                            'Seize the Season: Drive results by targeting consumers with specific seasonal interests, optimizing engagement when it matters most.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_seasonal'],
                        category: 'pelmorex_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return _.intersection(restrictedSegments, ['pds_seasonal']).length > 0;
                        },
                    },
                    // {
                    //     // For Audience page filtering by checkboxes and other uses
                    //     title: 'Personas',
                    //     label: 'Personas',
                    //     value: 'pelmorex_personas',
                    //     source: 'pds_personas',

                    //     // For Audience Tabs landing pages
                    //     type: 'Advanced Audiences',
                    //     link: '/audiences/personas',
                    //     icon: <img src="/images/audience-segments/icons/seasonal-card image.png" />, // Missing Icon
                    //     tooltip: 'Personas audiences', // Missing Description

                    //     // For segment picker filtering in ad form page
                    //     valuesForSegmentPicker: ['pds_personas'],
                    //     category: 'pelmorex_data',
                    //     shouldHide: ({ restrictedSegments }) => {
                    //         return _.intersection(restrictedSegments, ['pds_personas']).length > 0;
                    //     },
                    // },
                    // {
                    //     // For Audience page filtering by checkboxes and other uses
                    //     title: 'High Intent',
                    //     label: 'High Intent',
                    //     value: 'pelmorex_high_intent',
                    //     source: 'pds_high_intent',

                    //     // For Audience Tabs landing pages
                    //     type: 'Advanced Audiences',
                    //     link: '/audiences/high-intent',
                    //     icon: <img src="/images/audience-segments/icons/seasonal-card image.png" />, // Missing Icon
                    //     tooltip: 'High Intent audiences', // Missing Description

                    //     // For segment picker filtering in ad form page
                    //     valuesForSegmentPicker: ['pds_high_intent'],
                    //     category: 'pelmorex_data',
                    //     shouldHide: ({ restrictedSegments }) => {
                    //         return (
                    //             _.intersection(restrictedSegments, ['pds_high_intent']).length > 0
                    //         );
                    //     },
                    // },
                ],
            },
            { ...customAudiencesHierarchy },
        ],
    },
    {
        ...customAudiencesHierarchy,
    },
    {
        label: 'Partnerships',
        value: 'partnerships',
        // description:
        //     'Audiences pushed from third-party DMPs will appear here. Contact us for more information.',
        sections: [
            {
                title: 'Environics',
                label: 'Environics Audiences',
                value: 'environics_audiences',
                categories: [
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Prizm - Environics',
                        label: 'Prizm - Environics',
                        value: 'environics',
                        source: 'pds_environics',

                        // For Audience Tabs landing pages
                        link: '/audiences/environics',
                        type: 'Environics Audiences',
                        icon: (
                            <img src="/images/audience-segments/icons/prizm environics-card image.png" />
                        ),
                        tooltip:
                            'Psychographic Alignment: Connect with audiences by leveraging advanced lifestyle insights to resonate with personal interests and values.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_environics'],
                        category: 'partnerships',
                        shouldHide: ({ restrictedSegments }) => {
                            return (
                                _.intersection(restrictedSegments, ['pds_environics']).length > 0
                            );
                        },
                    },
                ],
            },
            {
                title: 'Manifold',
                label: 'Manifold Audiences',
                value: 'manifold_audiences',
                categories: [
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Manifold - Canacode',
                        label: 'CanaCode - Manifold',
                        value: 'canacode',
                        source: 'pds_manifold',

                        // For Audience Tabs landing pages
                        link: '/audiences/cana-code',
                        type: 'Manifold Audiences',
                        icon: (
                            <img src="/images/audience-segments/icons/manifold canacode-card image.png" />
                        ),
                        tooltip:
                            'Lifestyle Alignment: Utilize comprehensive socio-demographic insights to align with consumer behaviours and preferences.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['pds_manifold'],
                        category: 'partnerships',
                        shouldHide: ({ restrictedSegments }) => {
                            return _.intersection(restrictedSegments, ['pds_manifold']).length > 0;
                        },
                    },
                ],
            },
        ],
    },
    {
        label: 'Advertiser Data',
        value: 'advertiser_data',
        // description:
        //     'Audiences pushed from third-party DMPs will appear here. Contact us for more information.',
        sections: [
            {
                title: 'DMPs',
                label: 'DMPs',
                value: 'dmps',
                categories: [
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Adobe Audience Manager',
                        label: 'Adobe Audience Manager',
                        value: 'adobe',
                        source: 'adobe',

                        // For Audience Tabs landing pages
                        link: '/audiences/adobe',
                        type: 'DMPs',
                        img: '/images/AdobeAudienceManagerImage.png',
                        tooltip:
                            'Seamless Activation: Activate your CRM data securely to drive loyalty among your existing high-value users and activate lower funnel strategies.',
                        icon: (
                            <img src="/images/audience-segments/icons/adobe audience manager-card image.png" />
                        ),

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['adobe'],
                        category: 'advertiser_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return _.intersection(restrictedSegments, ['adobe']).length > 0;
                        },
                    },
                ],
            },
            {
                title: 'Hashed',
                label: 'Hashed',
                value: 'hashed',
                categories: [
                    {
                        // For Audience page filtering by checkboxes and other uses
                        title: 'Uploaded',
                        label: 'Uploaded',
                        value: 'uploaded',
                        source: 'upload',

                        // For Audience Tabs landing pages
                        link: '/audiences/uploaded',
                        type: 'Hashed',
                        icon: <img src="/images/audience-segments/icons/upload-card image.png" />,
                        tooltip:
                            'Secure Onboarding: Leverage first-party intelligence in a privacy-compliant environment to re-engage your current customer base and activate lower funnel strategies.',

                        // For segment picker filtering in ad form page
                        valuesForSegmentPicker: ['upload'],
                        category: 'advertiser_data',
                        shouldHide: ({ restrictedSegments }) => {
                            return _.intersection(restrictedSegments, ['upload']).length > 0;
                        },
                    },
                ],
            },
        ],
    },
];

export const AUDIENCE_SOURCE_TYPES_TO_UPDATE_STATUS = [
    'pds_custom_defined',
    'pds_custom_developed',
];

export const TORSTAR_ID = '60240676c48e547c2b3d114d';

// An active segment is one whose source campaign or ad is still running
export function isAudienceActive(audience) {
    return audience.status === 'active';
}

export const RETARGETING_TYPES = {
    Click: 'click',
    Impression: 'impression',
    Conversion: 'conversion',
    Attribution: 'attribution',
    Pixel: 'pixel',
};

export const STANDARD_RETARGETING_TYPES = [
    RETARGETING_TYPES.Click,
    RETARGETING_TYPES.Impression,
    RETARGETING_TYPES.Conversion,
];

export function getAudienceStatusClass(status, classes) {
    switch (status) {
        case AUDIENCE_STATUS_MAPPING.active:
        case AUDIENCE_STATUS_MAPPING.suspending:
            return classes.activeAudience;
        case AUDIENCE_STATUS_MAPPING.suspended:
        case AUDIENCE_STATUS_MAPPING.pending:
            return classes.pendingOrSuspendedAudience;
        default:
            return classes.activeAudience;
    }
}

// Newer, automated segments are easier to figure out. All PDS sourced segments are usable if they've been
// processed at least once, indicated by a value existing for `last_dmp_update`.
// Other segments may either:
// A) Have a match rate (as in the case of a retargeting segment), OR
// B) Lack a match rate, but are marked as processed. I'm not sure why this is the case.
export function isAudienceUsable(audience) {
    if (audience.source_type.match(/pds_/)) {
        return !!audience.last_dmp_update;
    }

    return typeof audience.match_rate === 'number' || audience.status === 'processed';
}

// A segment must contain both of these fields in order for matched users to be calculated
export function hasMatchInformation(audience) {
    return audience.uniques !== null && audience.match_rate !== null;
}

export function computeMatchedUsers(audience) {
    return floor(audience.uniques);
}

export function isDownloadEnabled(audience) {
    if (audience.mode !== 'retargeting') {
        return false;
    }

    if (['active', 'processed', 'processing'].indexOf(audience.status) === -1) {
        return false;
    }

    if (audience.uniques === 0 && audience.match_rate === 0) {
        return false;
    }

    return true;
}

/**
 * Get the label for the status of the audience
 * If it is suspending, we still show as active, but add extra info to the table
 * @param {*} audience
 * @returns string the label of the status for the user
 */
export function getAudienceStatus(audience) {
    if (isEmpty(audience.status)) {
        return 'N/A';
    }

    if (undefined === AUDIENCE_STATUS_MAPPING[audience.status]) {
        return audience.status;
    }
    return _.capitalize(AUDIENCE_STATUS_MAPPING[audience.status]);
}

/**
 * Get number of days the background job is expected to suspend the suspending audience
 * If negative (first day of implementation of this feature), return 0
 * @param {*} audience
 * @returns integer number of days from the current date
 */
export function getAudienceDaysToExpire(audience) {
    if (isEmpty(audience.expire_at)) {
        return 'N/A';
    }

    const expiration = moment(audience.expire_at).endOf('day');
    const today = moment().endOf('day');

    const days = expiration.diff(today, 'days');
    return days > 0 ? days : 0;
}

/**
 * Converts audience ID to MongoDB audience ID format.
 * PDS audiences have a hashed id like 46574e29-2f1b-489e-ba4b-5f4595215bdc and need the pds_ prefix.
 * Retargeting audiences have a numeric id like 1234 and don't need the pds_ prefix.
 * If the audience ID already has the pds_ prefix, it will not be added again.
 * Same as: urban/app/features/audiences/constants/index.js
 * @param {string} rawId - The audience ID to convert.
 * @returns {string} MongoDB audience ID with pds_ prefix for long IDs (if not already present), or original ID for short ones.
 */
export function parseMongoAudienceID(rawId) {
    if (rawId.startsWith('pds_')) {
        return rawId;
    }

    // Validates if ID looks like a UUID-style value consisting of 5 hyphen-separated alphanumeric groups.
    // Uses a relaxed regex (accepting any length for each group) instead of strict UUID 8-4-4-4-12 format,
    // to handle observed variable lengths in PDS data (e.g. 7-4-4-4-13).
    const looksLikeUUID = /^[a-z0-9]+(-[a-z0-9]+){4}$/i.test(rawId);
    if (looksLikeUUID) {
        return `pds_${rawId}`;
    }

    return rawId;
}
